import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLocale, type Locale } from '../locale/LocaleContext.js'
import { setAppLocale } from '../i18n/index.js'
import { useTheme, type ThemeChoice } from '../theme/ThemeContext.js'
import { useBasemapProvider, type BasemapProvider } from '../hooks/useBasemapProvider.js'
import { SegmentedControl } from '../components/ui/SegmentedControl.js'
import { Button } from '../components/ui/Button.js'
import {
  clearAllOfflinePacks,
  clearOfflinePack,
  getOfflineTileCacheUsage,
  listInstalledPacks,
  type OfflinePackMeta,
} from '../lib/offline-tiles.js'
import { subscribeOfflinePacksChanged } from '../lib/offline-pack-db.js'
import { formatBytes } from '../lib/formatBytes.js'

// SettingsPage — long-form settings home at /me/settings. Only settings with
// working backing behavior are surfaced here.

const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '0.0.0'
const DISPLAY_APP_VERSION = APP_VERSION && APP_VERSION !== '0.0.0' ? APP_VERSION : null

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

function isDisplayStandalone() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(display-mode: standalone)').matches
}

export function SettingsPage() {
  const { t, locale } = useLocale()
  const { choice, setChoice } = useTheme()
  const {
    provider: basemapProvider,
    setProvider: setBasemapProvider,
    tiandituAvailable,
  } = useBasemapProvider()
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isStandalone, setIsStandalone] = useState(() => isDisplayStandalone())
  const [offlineError, setOfflineError] = useState<string | null>(null)
  const [offlineNotice, setOfflineNotice] = useState<string | null>(null)
  const offlinePacks = useOfflinePacks(setOfflineError)

  useEffect(() => {
    document.title = `${t('settings')} · ${t('appName')}`
    return () => {
      document.title = t('appName')
    }
  }, [t])

  useEffect(() => {
    const media =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(display-mode: standalone)')
        : null
    const onChange = () => setIsStandalone(isDisplayStandalone())
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setInstallPrompt(event as BeforeInstallPromptEvent)
    }
    const onAppInstalled = () => {
      setIsStandalone(true)
      setInstallPrompt(null)
    }

    media?.addEventListener('change', onChange)
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)

    return () => {
      media?.removeEventListener('change', onChange)
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  async function installApp() {
    if (!installPrompt) return
    await installPrompt.prompt()
    await installPrompt.userChoice.catch(() => undefined)
    setInstallPrompt(null)
    setIsStandalone(isDisplayStandalone())
  }

  return (
    <div className="xr-page-container p-5 md:p-8">
      <header className="mb-6">
        <h1 className="text-xl font-semibold text-[var(--xr-text-primary)]">{t('settings')}</h1>
        <p className="text-xs text-[var(--xr-text-muted)]">
          <Link to="/me" className="hover:text-[var(--xr-text-primary)]">
            ← {t('accountMine')}
          </Link>
        </p>
      </header>

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <Section title={t('appearance')}>
          <SegmentedControl<ThemeChoice>
            value={choice}
            options={[
              { value: 'light', label: t('themeLight') },
              { value: 'dark', label: t('themeDark') },
              { value: 'system', label: t('themeAuto') },
            ]}
            onChange={setChoice}
            ariaLabel={t('appearance')}
          />
        </Section>

        <Section title={t('language')}>
          <SegmentedControl<Locale>
            value={locale}
            options={[
              { value: 'zh', label: '中文' },
              { value: 'en', label: 'EN' },
            ]}
            onChange={(next) => void setAppLocale(next)}
            ariaLabel={t('language')}
          />
        </Section>

        {tiandituAvailable && (
          <Section title={t('basemap.providerLabel')}>
            <SegmentedControl<BasemapProvider>
              value={basemapProvider}
              options={[
                { value: 'mapbox', label: t('basemap.mapbox') },
                { value: 'tianditu', label: t('basemap.tianditu') },
              ]}
              onChange={setBasemapProvider}
              ariaLabel={t('basemap.providerLabel')}
            />
            <p className="mt-2 text-xs text-[var(--xr-text-muted)]">
              {basemapProvider === 'mapbox'
                ? t('basemap.mapboxDescription')
                : t('basemap.tiandituDescription')}
            </p>
          </Section>
        )}

        <Section title={t('settings.installTitle')}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[var(--xr-text-secondary)]">
              {t('settings.installDescription')}
            </p>
            <Button
              type="button"
              variant={isStandalone ? 'secondary' : 'primary'}
              onClick={() => void installApp()}
              disabled={isStandalone || !installPrompt}
              className="shrink-0"
            >
              {isStandalone
                ? t('settings.installInstalled')
                : installPrompt
                  ? t('settings.installAction')
                  : t('settings.installUnavailable')}
            </Button>
          </div>
        </Section>

        <Section title={t('settings.offlineTitle')} className="lg:col-span-2">
          <div className="space-y-3">
            <div className="flex flex-col gap-2 text-sm text-[var(--xr-text-secondary)] sm:flex-row sm:items-center sm:justify-between">
              <p>
                {t('settings.offlineCache')} · {formatBytes(offlinePacks.cacheBytes)}
                {offlinePacks.storage && (
                  <>
                    {' · '}
                    {t('settings.offlineUsage')} {formatBytes(offlinePacks.storage.usage)}
                    {' · '}
                    {t('settings.offlineQuota')} {formatBytes(offlinePacks.storage.quota)}
                  </>
                )}
              </p>
              <Button
                type="button"
                variant="danger"
                size="sm"
                disabled={offlinePacks.packs.length === 0}
                onClick={() => {
                  if (!window.confirm(t('settings.offlineClearConfirm'))) return
                  setOfflineNotice(null)
                  void offlinePacks.clearAll().then((cleared) => {
                    if (cleared) setOfflineNotice(t('settings.offlineClearSuccess'))
                  })
                }}
              >
                {t('settings.offlineClearAll')}
              </Button>
            </div>
            {offlineError && (
              <p className="text-xs text-[var(--xr-danger)]" role="alert">
                {offlineError}
              </p>
            )}
            {offlineNotice && (
              <p className="text-xs text-[var(--xr-success)]" role="status">
                {offlineNotice}
              </p>
            )}
            {offlinePacks.packs.length === 0 ? (
              <p className="text-xs text-[var(--xr-text-muted)]">{t('offline.empty')}</p>
            ) : (
              <ul className="divide-y divide-[var(--xr-border-default)] overflow-hidden rounded-[var(--xr-radius-md)] border border-[var(--xr-border-default)]">
                {offlinePacks.packs.map((pack) => (
                  <li
                    key={`${pack.route_id}:${pack.style}`}
                    className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-[var(--xr-text-primary)]">
                        {pack.route_id} · {pack.style}
                      </p>
                      <p className="text-xs text-[var(--xr-text-muted)]">
                        z{pack.zoom_min}–{pack.zoom_max} · {pack.tile_count.toLocaleString()}{' '}
                        {t('settings.offlineTiles')} · {formatBytes(pack.byte_count)} ·{' '}
                        {new Date(pack.downloaded_at).toLocaleString()}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => void offlinePacks.deletePack(pack)}
                    >
                      {t('offline.delete')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Section>

        <Section title={t('about')} className="lg:col-span-2">
          <p className="text-sm text-[var(--xr-text-secondary)]">
            {t('appName')}
            {DISPLAY_APP_VERSION && (
              <>
                {' · '}
                <span className="tabular-nums text-[var(--xr-text-muted)]">
                  v{DISPLAY_APP_VERSION}
                </span>
              </>
            )}
          </p>
          <p className="mt-1 text-xs text-[var(--xr-text-muted)]">
            <a
              href="https://github.com/ww4yne/XingRoute"
              target="_blank"
              rel="noreferrer"
              className="hover:text-[var(--xr-text-primary)] underline-offset-2 hover:underline"
            >
              github.com/ww4yne/XingRoute
            </a>
          </p>
        </Section>
      </div>
    </div>
  )
}

function useOfflinePacks(onError?: (message: string | null) => void) {
  const [packs, setPacks] = useState<OfflinePackMeta[]>([])
  const [cacheBytes, setCacheBytes] = useState(0)
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null)

  async function refresh() {
    const [items, cacheUsage, estimate] = await Promise.all([
      listInstalledPacks().catch(() => []),
      getOfflineTileCacheUsage().catch(() => 0),
      navigator.storage?.estimate?.().catch(() => null) ?? Promise.resolve(null),
    ])
    setPacks(items.sort((a, b) => b.downloaded_at.localeCompare(a.downloaded_at)))
    setCacheBytes(cacheUsage)
    setStorage(
      estimate
        ? {
            usage: estimate.usage ?? 0,
            quota: estimate.quota ?? 0,
          }
        : null,
    )
  }

  useEffect(() => {
    void refresh()
    const unsubscribe = subscribeOfflinePacksChanged(() => void refresh())
    return unsubscribe
  }, [])

  return {
    packs,
    cacheBytes,
    storage,
    async deletePack(pack: OfflinePackMeta) {
      try {
        await clearOfflinePack(pack.route_id, pack.style)
        await refresh()
        onError?.(null)
      } catch (err) {
        onError?.(err instanceof Error ? err.message : String(err))
      }
    },
    async clearAll() {
      try {
        await clearAllOfflinePacks()
        await refresh()
        onError?.(null)
        return true
      } catch (err) {
        onError?.(err instanceof Error ? err.message : String(err))
        return false
      }
    },
  }
}

function Section({
  title,
  children,
  className = '',
}: {
  title: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <section
      className={`rounded-[var(--xr-radius-lg)] border border-[var(--xr-border-default)] bg-[var(--xr-bg-surface)] p-4 ${className}`}
    >
      <h2 className="mb-3 text-sm font-medium text-[var(--xr-text-primary)]">{title}</h2>
      {children}
    </section>
  )
}
